import { Box, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import EcgCanvasChart from '../ECGViewerCore/EcgCanvasChart';
import EcgToolbar from './EcgToolbar';
import { getProcessedRecord } from '../../../../../services/records';

const ECG_WINDOW_SECONDS = 10;
const ECG_BLOCK_SECONDS = 30;
const PREVIEW_PAGE_LIMIT = 5;
const FULL_RES_PAGE_LIMIT = 1;

const formatTimeUTC = (isoString) => {
    const d = new Date(isoString);
    return d.toLocaleTimeString('pt-PT', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'UTC'
    });
};

const timeToOffsetSeconds = (isoString, recordStartDate) => {
    const t = new Date(isoString);
    return Math.floor((t - recordStartDate) / 1000);
};

const getNextPageToken = (headers) =>
    headers?.['pagination-token'] ||
    headers?.['PAGINATION-TOKEN'] ||
    headers?.['Pagination-Token'] ||
    null;

const EcgViewer = ({ recordUuid, ecgTimeWindow, recordStart, recordEnd }) => {
    const recordStartDate = useMemo(() => new Date(recordStart), [recordStart]);
    const recordEndDate = useMemo(() => new Date(recordEnd), [recordEnd]);

    const totalSeconds = Math.max(
        0,
        Math.floor((recordEndDate - recordStartDate) / 1000)
    );

    const [viewportStartSec, setViewportStartSec] = useState(0);
    const [viewportDurationSec, setViewportDurationSec] = useState(ECG_WINDOW_SECONDS);
    const [activePageIndex, setActivePageIndex] = useState(0);
    const [activePageBlock, setActivePageBlock] = useState(null);
    const [cacheVersion, setCacheVersion] = useState(0);
    const [isBlockLoading, setIsBlockLoading] = useState(false);
    const [blockError, setBlockError] = useState(null);

    const pageCacheRef = useRef(new Map());
    const pageTokensRef = useRef([null]);
    const hasMorePagesRef = useRef(true);
    const inFlightPagesRef = useRef(new Set());
    const lastRequestedPageRef = useRef(null);
    const activePageIndexRef = useRef(0);

    useEffect(() => {
        activePageIndexRef.current = activePageIndex;
    }, [activePageIndex]);

    const {
        data: previewBlocks = [],
        isLoading: isPreviewLoading,
        error: previewError
    } = useQuery({
        queryKey: ['record-ecg-preview-all', recordUuid],
        enabled: Boolean(recordUuid),
        queryFn: async () => {
            let token = null;
            let hasNext = true;
            const allBlocks = [];
            const seenTokens = new Set();
            let safetyCounter = 0;
            const MAX_PREVIEW_REQUESTS = 200;

            while (hasNext) {
                safetyCounter += 1;
                if (safetyCounter > MAX_PREVIEW_REQUESTS) break;

                if (token && seenTokens.has(token)) break;
                if (token) seenTokens.add(token);

                const resp = await getProcessedRecord({
                    uuid: recordUuid,
                    dtype: 'ECG-Preview',
                    pageLimit: PREVIEW_PAGE_LIMIT,
                    pageToken: token
                });

                allBlocks.push(...(resp?.data || []));
                const nextToken = getNextPageToken(resp?.headers);
                if (nextToken && nextToken === token) break;
                token = nextToken;
                hasNext = Boolean(token);
            }

            return allBlocks;
        }
    });

    const fetchFullResPage = useCallback(
        async (targetPageIndex) => {
            if (!recordUuid || targetPageIndex < 0) return null;

            if (pageCacheRef.current.has(targetPageIndex)) {
                return pageCacheRef.current.get(targetPageIndex);
            }

            for (let page = 0; page <= targetPageIndex; page += 1) {
                if (pageCacheRef.current.has(page)) continue;

                const token = pageTokensRef.current[page] ?? null;
                if (page > 0 && token == null && !hasMorePagesRef.current) {
                    return null;
                }

                const resp = await getProcessedRecord({
                    uuid: recordUuid,
                    dtype: 'ECG',
                    pageLimit: FULL_RES_PAGE_LIMIT,
                    pageToken: token
                });

                const blocks = resp?.data || [];
                const nextToken = getNextPageToken(resp?.headers);

                pageTokensRef.current[page + 1] = nextToken;
                if (!nextToken) hasMorePagesRef.current = false;

                if (!blocks.length) return null;
                pageCacheRef.current.set(page, blocks[0]);
                setCacheVersion((v) => v + 1);
            }

            return pageCacheRef.current.get(targetPageIndex) || null;
        },
        [recordUuid]
    );

    const loadPage = useCallback(
        async (pageIndex) => {
            if (pageIndex < 0) return;
            if (inFlightPagesRef.current.has(pageIndex)) return;
            if (lastRequestedPageRef.current === pageIndex && pageCacheRef.current.has(pageIndex)) return;

            inFlightPagesRef.current.add(pageIndex);
            lastRequestedPageRef.current = pageIndex;
            setIsBlockLoading(true);
            setBlockError(null);
            try {
                const block = await fetchFullResPage(pageIndex);
                if (!block) return;
                setActivePageIndex(pageIndex);
                setActivePageBlock(block);
            } catch (err) {
                setBlockError(err);
            } finally {
                inFlightPagesRef.current.delete(pageIndex);
                setIsBlockLoading(false);
            }
        },
        [fetchFullResPage]
    );

    const prefetchPage = useCallback(
        async (pageIndex) => {
            if (pageIndex < 0) return;
            if (pageCacheRef.current.has(pageIndex)) return;
            if (inFlightPagesRef.current.has(pageIndex)) return;

            inFlightPagesRef.current.add(pageIndex);
            try {
                await fetchFullResPage(pageIndex);
            } catch (err) {
                // Prefetch is best-effort; ignore and retry later if needed.
            } finally {
                inFlightPagesRef.current.delete(pageIndex);
            }
        },
        [fetchFullResPage]
    );

    useEffect(() => {
        if (!recordUuid) return;

        pageCacheRef.current = new Map();
        pageTokensRef.current = [null];
        hasMorePagesRef.current = true;
        inFlightPagesRef.current = new Set();
        lastRequestedPageRef.current = null;
        setActivePageIndex(0);
        setActivePageBlock(null);
        setCacheVersion(0);
        setViewportStartSec(0);
        setViewportDurationSec(ECG_WINDOW_SECONDS);
        setBlockError(null);
        loadPage(0);
    }, [recordUuid, loadPage]);

    const previewSamplingRate =
        Number(previewBlocks?.[0]?.mdata?.sampling_rate) > 0
            ? Number(previewBlocks?.[0]?.mdata?.sampling_rate)
            : 13;

    const ecgStartOffsetSec = useMemo(() => {
        if (!previewBlocks?.length || !previewBlocks[0]?.ts) return 0;
        const offset = timeToOffsetSeconds(previewBlocks[0].ts, recordStartDate);
        return Number.isFinite(offset) ? Math.max(0, offset) : 0;
    }, [previewBlocks, recordStartDate]);

    const overviewSamples = useMemo(() => {
        if (!previewBlocks.length) return [];
        return previewBlocks.flatMap((block) => block?.data?.ECG || []);
    }, [previewBlocks]);

    useEffect(() => {
        if (!ecgTimeWindow?.startIso || !recordStartDate) return;

        const offset =
            timeToOffsetSeconds(ecgTimeWindow.startIso, recordStartDate) -
            ecgStartOffsetSec;
        const safeStart = Math.max(0, Math.min(offset, totalSeconds - ECG_WINDOW_SECONDS));
        setViewportStartSec(safeStart);
        setViewportDurationSec(ECG_WINDOW_SECONDS);

        const targetPage = Math.floor(safeStart / ECG_BLOCK_SECONDS);
        if (targetPage !== activePageIndex) {
            loadPage(targetPage);
        }
    }, [
        ecgTimeWindow?.startIso,
        recordStartDate,
        totalSeconds,
        activePageIndex,
        loadPage,
        ecgStartOffsetSec
    ]);

    const viewportBundle = useMemo(() => {
        const basePageIndex = Math.max(0, Math.floor(viewportStartSec / ECG_BLOCK_SECONDS));
        const offsetInBasePage = viewportStartSec - basePageIndex * ECG_BLOCK_SECONDS;
        const pagesNeeded = Math.max(
            1,
            Math.ceil((offsetInBasePage + viewportDurationSec) / ECG_BLOCK_SECONDS)
        );

        const blocks = [];
        for (let i = 0; i < pagesNeeded; i += 1) {
            const block = pageCacheRef.current.get(basePageIndex + i);
            if (!block) break;
            blocks.push(block);
        }

        const firstBlock = blocks[0] || activePageBlock;
        const samplingRate =
            Number(firstBlock?.mdata?.sampling_rate) > 0
                ? Number(firstBlock?.mdata?.sampling_rate)
                : 130;

        const samples = blocks.flatMap((block) => block?.data?.ECG || []);

        let signalStartSec = basePageIndex * ECG_BLOCK_SECONDS;
        if (firstBlock?.ts) {
            const offset = timeToOffsetSeconds(firstBlock.ts, recordStartDate);
            if (Number.isFinite(offset)) {
                signalStartSec = Math.max(0, offset - ecgStartOffsetSec);
            }
        }

        return {
            samples: samples.length ? samples : activePageBlock?.data?.ECG || [],
            samplingRate,
            signalStartSec
        };
    }, [
        viewportStartSec,
        viewportDurationSec,
        activePageBlock,
        recordStartDate,
        ecgStartOffsetSec,
        cacheVersion
    ]);

    const activeTimeWindow = useMemo(() => {
        const startIso = new Date(
            recordStartDate.getTime() + (ecgStartOffsetSec + viewportStartSec) * 1000
        ).toISOString();
        const endIso = new Date(
            recordStartDate.getTime() +
                (ecgStartOffsetSec + viewportStartSec + viewportDurationSec) * 1000
        ).toISOString();

        return {
            startIso: formatTimeUTC(startIso),
            endIso: formatTimeUTC(endIso)
        };
    }, [recordStartDate, viewportStartSec, viewportDurationSec, ecgStartOffsetSec]);

    const handleViewportChange = useCallback(
        (viewport) => {
            if (!viewport) return;
            const incomingDuration =
                Number(viewport.durationSec) > 0
                    ? viewport.durationSec
                    : ECG_WINDOW_SECONDS;
            const safeStart = Math.max(
                0,
                Math.min(viewport.startSec ?? 0, Math.max(0, totalSeconds - incomingDuration))
            );
            const safeDuration =
                Number(incomingDuration) > 0
                    ? Math.min(incomingDuration, Math.max(1, totalSeconds || ECG_WINDOW_SECONDS))
                    : ECG_WINDOW_SECONDS;

            setViewportStartSec(safeStart);
            setViewportDurationSec(safeDuration);
            const targetPage = Math.floor(safeStart / ECG_BLOCK_SECONDS);
            if (targetPage !== activePageIndexRef.current) {
                loadPage(targetPage);
            }

            const viewportEnd = safeStart + safeDuration;
            const nextPage = Math.floor(viewportEnd / ECG_BLOCK_SECONDS);
            if (nextPage > targetPage) {
                prefetchPage(nextPage);
            }
        },
        [totalSeconds, loadPage, prefetchPage]
    );

    if (isPreviewLoading || (isBlockLoading && !activePageBlock)) {
        return (
            <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (previewError || blockError) {
        return <Typography color="error">Erro ao carregar ECG</Typography>;
    }

    return (
        <>
            <EcgToolbar ecgTimeWindow={activeTimeWindow} />

            <EcgCanvasChart
                overviewSamples={overviewSamples}
                overviewSamplingRate={previewSamplingRate}
                viewportSamples={viewportBundle.samples}
                viewportSamplingRate={viewportBundle.samplingRate}
                viewportSignalStartSec={viewportBundle.signalStartSec}
                viewportStartSec={viewportStartSec}
                viewportDurationSec={viewportDurationSec}
                onViewportChange={handleViewportChange}
            />
        </>
    );
};

export default EcgViewer;
